import { createSelector } from '@reduxjs/toolkit'
import { AppDispatch, AppGetState, RootState } from '..'
import { Box3, Vector3, Vector3Like } from 'three'
import { denormalizeWalls, wallContainingPoint } from '~/components/DrawingCanvas/lib/collisionMeshes'
import { Obstruction, PartialObject, Product } from './types'
import ObjectClassName from '~/config/objectClassNames'
import { updateObjects } from './actions'

type SelectedItem =
  | {
      id: string
      className: 'Product'
      data: Product
    }
  | {
      id: string
      className: 'Obstruction'
      data: Obstruction
    }

const selectSelectedItems = createSelector(
  (state: RootState) => state.objects.present.products,
  (state: RootState) => state.objects.present.obstructions,
  (state: RootState) => state.selectedObjects,
  (products, obstructions, selected) => {
    return selected
      .filter(data => data.id in products || data.id in obstructions)
      .map<SelectedItem>(
        data =>
          ({
            ...data,
            data: data.id in products ? products[data.id] : obstructions[data.id],
          }) as SelectedItem
      )
  }
)

export const selectNumberCenterable = createSelector(
  (state: RootState) => state.objects.present.products,
  (state: RootState) => state.objects.present.obstructions,
  (state: RootState) => state.selectedObjects,
  (products, obstructions, selected) => {
    return selected.filter(data => data.id in products || data.id in obstructions).length
  }
)

function createUpdate(selected: SelectedItem, delta: Vector3Like): PartialObject<Product> | PartialObject<Obstruction> {
  if (selected.className === ObjectClassName.PRODUCT) {
    return {
      id: selected.id,
      position: {
        x: selected.data.position.x + delta.x,
        y: selected.data.position.y + delta.y,
        z: selected.data.position.z + delta.z,
      },
    }
  } else if (selected.className === ObjectClassName.OBSTRUCTION) {
    return {
      id: selected.id,
      position: {
        x: selected.data.position.x + delta.x,
        y: selected.data.position.y + delta.y,
        z: selected.data.position.z + delta.z,
      },
      positions: selected.data.positions.map(position => ({
        x: position.x + delta.x,
        y: position.y + delta.y,
        z: position.z + delta.z,
      })),
    }
  } else {
    // @ts-expect-error we are handling the edge case here
    throw new Error(`unhandled update class name ${selected.className}`)
  }
}

export const centerSelected = (axis: 'x' | 'y') => (dispatch: AppDispatch, getState: AppGetState) => {
  const selectedItems = selectSelectedItems(getState())
  if (selectedItems.length < 1) {
    return
  }

  const minX = selectedItems.reduce((x, item) => Math.min(item.data.position.x, x), +Infinity)
  const maxX = selectedItems.reduce((x, item) => Math.max(item.data.position.x, x), -Infinity)
  const minY = selectedItems.reduce((y, item) => Math.min(item.data.position.y, y), +Infinity)
  const maxY = selectedItems.reduce((y, item) => Math.max(item.data.position.y, y), -Infinity)

  const box = new Box3(new Vector3(minX, minY), new Vector3(maxX, maxY))

  const centerPoint = box.getCenter(new Vector3())

  const { segments, objects } = getState().objects.present

  const wallId = wallContainingPoint(centerPoint, objects, segments)
  if (wallId === null) {
    return
  }

  const denormalizedSegments = denormalizeWalls(segments, objects)
    .map(({ segments }) => segments)
    .flat()
  const segmentPoints = denormalizedSegments
    .filter(segment => segment.parentId === wallId)
    .flatMap(segment => [segment.insetEdge.start, segment.insetEdge.end])

  const minSegmentX = segmentPoints.reduce((x, point) => Math.min(point.x, x), +Infinity)
  const maxSegmentX = segmentPoints.reduce((x, point) => Math.max(point.x, x), -Infinity)
  const minSegmentY = segmentPoints.reduce((y, point) => Math.min(point.y, y), +Infinity)
  const maxSegmentY = segmentPoints.reduce((y, point) => Math.max(point.y, y), -Infinity)

  const segmentBox = new Box3(new Vector3(minSegmentX, minSegmentY), new Vector3(maxSegmentX, maxSegmentY))

  const centerSegmentPoint = segmentBox.getCenter(new Vector3())

  const difference = new Vector3().subVectors(centerSegmentPoint, centerPoint)

  if (axis === 'x') {
    difference.setY(0)
  } else if (axis === 'y') {
    difference.setX(0)
  } else {
    const _: never = axis
    throw new Error(`unknown axis ${axis}`)
  }

  const updates = selectedItems.map(item => createUpdate(item, difference))

  dispatch(updateObjects(updates))
}
